import { FC, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useState } from "react";
import { useMutation } from "react-query";

import { sendEmailSchema } from "@/helpers/validators";
import { Props } from "./types";
import Button from "@/atoms/Button";
import { SendMessage } from "@/services/apiTypes";
import { sendMessage } from "@/services/api";

import getConfig from "next/config";

const { publicRuntimeConfig } = getConfig();

const Form: FC<Props> = ({ className }) => {
  const [showFeedback, setShowFeedback] = useState(false);
  const [isMutating, setIsMutating] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [value, setValue] = useState<string>();

  const textAreaChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue(event.target.value);
  };

  const {
    handleSubmit,
    formState: { errors, touchedFields },
    register,
    reset,
  } = useForm<SendMessage>({
    resolver: yupResolver(sendEmailSchema),
  });

  const { ref, ...rest } = register("message", { required: true });

  const { mutate } = useMutation(sendMessage, {
    onSuccess: () => {
      setShowFeedback(true);
      setTimeout(() => setShowFeedback(false), 5000);
      reset();
    },
    onMutate: () => setIsMutating(true),
    onSettled: () => setIsMutating(false),
  });

  const onSubmit = (data: SendMessage) => {
    mutate(data);
  };

  useEffect(() => {
    if (textareaRef && textareaRef.current) {
      textareaRef.current.style.height = "2.3rem";
      const scrollHeight = textareaRef.current.scrollHeight;
      textareaRef.current.style.height = scrollHeight + "px";
    }
  }, [value]);

  return (
    <div className={`${className}`}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="relative flex flex-col xl:top-2">
          <div className="flex flex-col justify-start w-full md:flex-row ">
            <div className="relative flex-grow">
              <input
                {...register("name", { required: true })}
                id="name"
                name="name"
                type="text"
                className={`w-full h-10  border-b-2 text-landing-blue border-landing-blue peer focus:outline-none placeholder:opacity-0 focus:placeholder:opacity-100 ${
                  errors.name
                    ? "focus:border-error-value border-error-value"
                    : "focus:border-landing-denim border-landing-blue"
                }`}
                placeholder="e.g. Julie Smith"
              />
              <label
                htmlFor="name"
                className={`select-none pointer-events-none absolute left-0 -top-4 text-sm transition-all peer-placeholder-shown:text-base  peer-placeholder-shown:top-2 peer-focus:-top-4 ${
                  errors.name
                    ? "peer-focus:text-error-value text-error-value"
                    : "peer-focus:text-landing-denim text-landing-blue peer-placeholder-shown:text-landing-blue"
                } peer-focus:text-sm`}
              >
                Name
              </label>

              <p
                className={`mt-2 h-5 text-error-value transition-opacity duration-300 ease-in-out text-xs xs:text-sm ${
                  errors.name ? "opacity-100" : "opacity-0"
                }`}
              >
                {touchedFields.name && errors.name ? errors.name.message : ""}
              </p>
            </div>

            <div className="relative flex-grow mt-7 md:ml-5 md:mt-0">
              <input
                {...register("email", { required: true })}
                id="email"
                name="email"
                type="text"
                className={`w-full h-10 placeholder:opacity-0 focus:placeholder:opacity-100 border-b-2 text-landing-blue border-landing-blue peer focus:outline-none ${
                  errors.email
                    ? "focus:border-error-value border-error-value"
                    : "focus:border-landing-denim border-landing-blue"
                }`}
                placeholder="e.g. julie.smith@example.com"
              />
              <label
                htmlFor="email"
                className={`select-none pointer-events-none absolute left-0 -top-4 text-sm transition-all peer-placeholder-shown:text-base  peer-placeholder-shown:top-2 peer-focus:-top-4 ${
                  errors.email
                    ? "peer-focus:text-error-value text-error-value"
                    : "peer-focus:text-landing-denim text-landing-blue peer-placeholder-shown:text-landing-blue"
                } peer-focus:text-sm`}
              >
                Email
              </label>

              <p
                className={`mt-2 h-5  text-xs xs:text-sm text-error-value transition-opacity duration-300 ease-in-out opacity-0 ${
                  errors.email ? "opacity-100" : "opacity-0"
                }`}
              >
                {touchedFields.email && errors.email ? errors.email.message : ""}
              </p>
            </div>
          </div>

          <div className="relative mt-7">
            <textarea
              {...rest}
              ref={(e) => {
                ref(e);
                textareaRef.current = e;
              }}
              onChange={textAreaChange}
              id="message"
              name="message"
              className={`w-full pr-2 max-h-24 pb-2 placeholder:opacity-0 focus:placeholder:opacity-100 border-b-2 text-landing-blue border-landing-blue peer resize-none focus:outline-none ${
                errors.message
                  ? "focus:border-error-value border-error-value"
                  : "focus:border-landing-denim border-landing-blue"
              }`}
            />
            <label
              htmlFor="message"
              className={`select-none pointer-events-none absolute left-0 -top-6 text-sm transition-all peer-placeholder-shown:text-base  peer-placeholder-shown:top-0 peer-focus:-top-6 ${
                errors.message
                  ? "peer-focus:text-error-value text-error-value"
                  : "peer-focus:text-landing-denim text-landing-blue peer-placeholder-shown:text-landing-blue"
              } peer-focus:text-sm`}
            >
              Message
            </label>

            <p
              className={`mt-2 h-5 text-xs xs:text-sm  text-error-value transition-opacity duration-300 ease-in-out opacity-0 ${
                errors.message ? "opacity-100" : "opacity-0"
              }`}
            >
              {touchedFields.message && errors.message ? errors.message.message : ""}
            </p>
          </div>
        </div>

        <p
          className={`transition duration-300 h-4 text-sm text-success -top-3 relative ${
            showFeedback ? "opacity-100" : "opacity-0"
          }`}
        >
          {publicRuntimeConfig.FEATURE_HOLIDAYS_DISRUPTIONS !== "1"
            ? "Message has been sent successfully."
            : "Message has been sent successfully. The Keyzy team will review your email and get back to you soon."}
        </p>

        <Button
          type="submit"
          className="relative z-10 h-12 mt-4 w-full md:w-[4.5rem] hover:scale-105 transition duration-300 ease-in-out transform "
          isLoading={isMutating}
        >
          Send
        </Button>
      </form>
    </div>
  );
};

export default Form;
